import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BellOutlined,
  ShoppingCartOutlined,
  DollarOutlined,
  InboxOutlined,
  GiftOutlined,
  SyncOutlined,
  RocketOutlined,
  SafetyCertificateOutlined,
  MessageOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import clsx from 'clsx';
import { Button } from 'antd';
import { AppNotification } from '@app/api/notifications.api';

interface NotificationsOverlayProps {
  notifications: AppNotification[];
  onMarkAllRead: () => void;
  onMarkOneRead: (id: number) => Promise<void>;
}

interface KindConfig {
  colorClass: string;
  icon: React.ReactNode;
  label?: string;
}

const KIND_CONFIG: Record<string, KindConfig> = {
  order_created: { colorClass: 'tw-text-primary', icon: <ShoppingCartOutlined />, label: 'Đơn hàng mới' },
  order_status_changed: { colorClass: 'tw-text-success-teal', icon: <SyncOutlined />, label: 'Cập nhật trạng thái' },
  payment: { colorClass: 'tw-text-success-teal', icon: <DollarOutlined />, label: 'Thanh toán' },
  stock_import: { colorClass: 'tw-text-warning', icon: <InboxOutlined />, label: 'Nhập kho' },
  birthday: { colorClass: 'tw-text-secondary', icon: <GiftOutlined />, label: 'Sinh nhật' },
  release: { colorClass: 'tw-text-primary', icon: <RocketOutlined />, label: 'Phiên bản mới' },
  security: { colorClass: 'tw-text-error', icon: <SafetyCertificateOutlined />, label: 'Cảnh báo bảo mật' },
  message_received: { colorClass: 'tw-text-secondary', icon: <MessageOutlined />, label: 'Tin nhắn mới' },
  leave_request: { colorClass: 'tw-text-primary', icon: <CalendarOutlined />, label: 'Nghỉ phép / chấm công' },
};

const DEFAULT_KIND: KindConfig = { colorClass: 'tw-text-on-surface-variant', icon: <BellOutlined /> };

export const NotificationsOverlay: React.FC<NotificationsOverlayProps> = ({
  notifications,
  onMarkAllRead,
  onMarkOneRead,
}) => {
  const navigate = useNavigate();
  const unreadCount = notifications.filter((n) => !n.read).length;

  const handleClick = async (n: AppNotification) => {
    if (!n.read) await onMarkOneRead(n.id);
    if (n.url) navigate(n.url);
  };

  return (
    <div className="tw-w-[360px] tw-max-w-[86vw]">
      <div className="tw-mb-1 tw-flex tw-items-center tw-justify-between tw-gap-2 tw-px-1 tw-pt-1">
        <span className="tw-font-heading tw-text-sm tw-font-semibold tw-text-on-surface">
          Thông báo {unreadCount > 0 && `(${unreadCount})`}
        </span>
        {unreadCount > 0 && (
          <Button type="link" size="small" className="tw-px-1 tw-text-xs" onClick={onMarkAllRead}>
            Đánh dấu tất cả đã đọc
          </Button>
        )}
      </div>

      <div className="tw-flex tw-max-h-[420px] tw-flex-col tw-gap-0.5 tw-overflow-y-auto tw-overflow-x-hidden">
        {notifications.length > 0 ? (
          notifications.map((n) => {
            const cfg = KIND_CONFIG[n.kind] ?? DEFAULT_KIND;
            return (
              <div
                key={n.id}
                onClick={() => handleClick(n)}
                className={clsx(
                  'tw-flex tw-cursor-pointer tw-items-start tw-gap-3 tw-rounded-lg tw-px-2.5 tw-py-2.5 tw-transition-colors hover:tw-bg-surface-container-low',
                  !n.read && 'tw-bg-surface-container-low',
                )}
              >
                <div
                  className={clsx(
                    'tw-flex tw-h-9 tw-w-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-surface-container tw-text-base',
                    cfg.colorClass,
                  )}
                >
                  {cfg.icon}
                </div>
                <div className="tw-min-w-0 tw-flex-1">
                  <div
                    className={clsx(
                      'tw-text-[13px] tw-leading-snug tw-text-on-surface',
                      n.read ? 'tw-font-normal' : 'tw-font-semibold',
                    )}
                  >
                    {n.title}
                    {cfg.label && (
                      <span className="tw-ml-1 tw-text-xs tw-font-normal tw-text-on-surface-variant">
                        ({cfg.label})
                      </span>
                    )}
                  </div>
                  {n.body && (
                    <div className="tw-mt-0.5 tw-truncate tw-text-xs tw-text-on-surface-variant">{n.body}</div>
                  )}
                  <div className="tw-mt-1 tw-text-[11px] tw-text-outline">{n.created_at}</div>
                </div>
                {!n.read && <span className="tw-mt-1.5 tw-h-2 tw-w-2 tw-shrink-0 tw-rounded-full tw-bg-primary" />}
              </div>
            );
          })
        ) : (
          <div className="tw-py-8 tw-text-center">
            <BellOutlined className="tw-mb-2 tw-block tw-text-[32px] tw-text-outline-variant" />
            <span className="tw-text-[13px] tw-text-on-surface-variant">Không có thông báo</span>
          </div>
        )}
      </div>
    </div>
  );
};
